import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { PopupRequest } from '@azure/msal-browser/dist/request/PopupRequest';
import { Observable, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { catchErrorLogRet } from '../../common/rest.operators';
import { MsftFrontendConfig } from '../core';

import { parseWwwAuthenticate } from './parse-www-authenticate.function';

/** TODO revise. this could be generic and could move to a core module */
@Injectable()
export class AuthService implements OnDestroy {
    gettingToken: boolean | null;
    tokenChange: Observable<string | null>;

    private readonly _tokenChangeSubj: Subject<string | null>;

    private _msftConfig: MsftFrontendConfig;
    private _redirectUri: string;
    private _tokenClient: PublicClientApplication;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._tokenChangeSubj = new Subject();
        this.tokenChange = this._tokenChangeSubj.asObservable();
    }

    ngOnDestroy(): void {
        this._tokenChangeSubj.complete();
    }

    async getToken(userId: number): Promise<string | null> {
        if (this.gettingToken === true) {
            console.error('already getting token');
            return null;
        }

        if (
            this._msftConfig?.clientId == null ||
            this._msftConfig?.scopes == null ||
            this._redirectUri == null
        ) {
            this.gettingToken = true;

            this._msftConfig = await this._http
                .get(
                    `${this._appConfig.API_ENDPOINTS.TEAMS_CALL}/microsoft/token/config`,
                )
                .pipe(catchErrorLogRet<MsftFrontendConfig>(null))
                .toPromise();

            this._redirectUri = `${window.location.protocol}//${window.location.host}/`;

            this._tokenClient = new PublicClientApplication({
                auth: {
                    clientId: this._msftConfig?.clientId,
                    redirectUri: this._redirectUri,
                    authority: `https://login.microsoftonline.com/${
                        this._msftConfig?.tenantId ?? ''
                    }/`,
                },
            });
        }

        if (
            this._msftConfig?.clientId == null ||
            this._msftConfig?.scopes == null ||
            this._redirectUri == null
        ) {
            this.gettingToken = false;
            console.error('clientId or redirectUrl or scopes undefined');
            return null;
        }

        this.gettingToken = true;

        const token = await this._verifyBackendConsent(
            userId,
            (await this._login())?.accessToken,
        );

        this.gettingToken = false;
        this._tokenChangeSubj.next(token);

        return token;
    }

    private async _login(): Promise<AuthenticationResult> {
        const acc = this._tokenClient.getAllAccounts()?.[0];
        if (acc != null) {
            try {
                this._tokenClient.setActiveAccount(acc);
                return await this._tokenClient.acquireTokenSilent({
                    scopes: this._msftConfig?.scopes,
                });
            } catch (err) {
                console.log(err);
            }
        }

        try {
            return await this._tokenClient.loginPopup({
                scopes: this._msftConfig?.scopes,
            });
        } catch (err) {
            console.log(err);
        }

        return null;
    }

    private async _verifyBackendConsent(
        userId: number,
        accessToken: string | null,
    ): Promise<string | null> {
        try {
            const resp = await this._http
                .post(
                    `${this._appConfig.API_ENDPOINTS.TEAMS_CALL}/microsoft/token/verify-consent`,
                    {
                        userId,
                        accessToken,
                    },
                    { observe: 'response' },
                )
                .pipe(
                    catchError((err) => {
                        return of(err);
                    }),
                )
                .toPromise();

            if (resp.status === 200) {
                return accessToken;
            }

            if (resp.status !== 403) {
                return null;
            }

            const headerStr = resp.headers.get('WWW-Authenticate');
            const opts = parseWwwAuthenticate(headerStr);

            if (opts?.scopes == null || opts.scopes.length === 0) {
                return null;
            }

            const backInst = new PublicClientApplication({
                auth: {
                    clientId: opts.clientId,
                    authority: opts.authority,
                    redirectUri: this._redirectUri,
                },
            });

            const loginReq: PopupRequest = {
                scopes: opts.scopes,
                prompt: opts.proposedAction ?? 'consent',
            };

            return (await backInst.loginPopup(loginReq))?.accessToken;
        } catch (err) {
            // TODO ask user to try again since if loginPopup() was executed for the spa-app-clientId within the same callstack, the second loginPopup() may not run
            console.log(err);
        }

        return null;
    }
}
